import { useAppointmentsStore } from '../stores/useAppointmentsStore';
import { useInvoicesStore } from '../stores/useInvoicesStore';
import { useQuotesStore } from '../stores/useQuotesStore';

/**
 * Orchestrates the three consumer-report fetches that drive nav badges.
 * Fired on auth success (App.vue onBeforeMount) without awaiting so the
 * post-login redirect is not blocked. Each store handles its own errors
 * internally (the catch in fetchReport sets status='error' and preserves
 * any previously loaded data), so this composable does not throw and
 * Promise.allSettled is used only as a defensive guard against a future
 * store action that surfaces an error instead of swallowing it.
 *
 * Cache strategy: no TTL. Stores hold data until explicitly invalidated
 * by mutating actions (cancel, schedule, future pay/accept) or until a
 * caller invokes refresh() / refreshAll(). ETag caching at the axios
 * layer makes refetches cheap.
 *
 * See .planning/followups/nav-badges-and-report-fan-out.md.
 */
export interface ReportsFanOut {
  fetchAll: () => Promise<void>;
  refreshAll: () => Promise<void>;
  invalidateAll: () => void;
}

export function useReportsFanOut(): ReportsFanOut {
  const appointmentsStore = useAppointmentsStore();
  const invoicesStore = useInvoicesStore();
  const quotesStore = useQuotesStore();

  async function fetchAll(): Promise<void> {
    await Promise.allSettled([
      appointmentsStore.fetchReport(),
      invoicesStore.fetchReport(),
      quotesStore.fetchReport(),
    ]);
  }

  async function refreshAll(): Promise<void> {
    // refreshAll is an alias today. If TTL / stale-while-revalidate ever
    // lands, fetchAll will become "if not fetched, fetch" and refreshAll
    // will become "force refetch." Document the future split here so the
    // call site does not have to change.
    await fetchAll();
  }

  function invalidateAll(): void {
    appointmentsStore.invalidateReport();
    invoicesStore.invalidate();
    quotesStore.invalidate();
  }

  return {
    fetchAll,
    refreshAll,
    invalidateAll,
  };
}
